import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import {
  ArrowRight, Phone, Mail, Droplets, Sparkles, Trophy, Sofa, Boxes,
  Award, Store, Gauge, LifeBuoy, CheckCircle2,
} from "lucide-react";

const CATEGORIES = [
  { i: Droplets, t: "Premium Cleaning & Hygiene", items: ["Diversey Chemicals", "Reckitt Benckiser", "Essential cleaning materials"] },
  { i: Sparkles, t: "Efficient Housekeeping", items: ["Advanced housekeeping tools", "State-of-the-art machinery"] },
  { i: Trophy, t: "Sports & Recreation", items: ["High-performance sports kits", "Quality sports material"] },
  { i: Sofa, t: "Comfort & Convenience", items: ["Hotel & office furniture", "Tissue paper & napkins", "Toiletries & amenities"] },
  { i: Boxes, t: "Comprehensive Solutions", items: ["Everything for hotels & restaurants", "Expert housekeeping services", "Manpower management"] },
];

const BENEFITS = [
  { i: Award, t: "Uncompromising Quality", d: "We source and supply only the best brands and products." },
  { i: Store, t: "One-Stop Solution", d: "Everything you need under one roof, simplifying procurement." },
  { i: Gauge, t: "Operational Excellence", d: "Boost efficiency and cleanliness with specialised tools and services." },
  { i: LifeBuoy, t: "Dedicated Support", d: "Our team is committed to meeting your unique business needs." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="grid-bg">
        <div className="max-w-7xl mx-auto px-6 md:px-8 pt-8 md:pt-10">
          <div className="w-full rounded-md border border-gray-200 overflow-hidden" data-testid="hero-banner-wrap">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/196fd693-3c3b-4453-9118-3cc0d8ac9ebd/images/ff9fa54ccca92220642cab82fc37cc71713b80733b03ae87fa4c9c51fceb5942.png"
              alt="Commercial cleaning supplies for offices and facilities"
              className="w-full h-52 sm:h-64 md:h-80 object-cover object-[50%_32%]"
              data-testid="hero-banner"
            />
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-16 md:py-20 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <p className="text-xs uppercase tracking-[0.2em] text-[#0e5a32] mb-4 font-medium" data-testid="hero-overline">Hospitality &amp; Housekeeping Supplies · Since 2020</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-semibold tracking-tighter text-gray-900 leading-tight mb-6" data-testid="hero-title">
              Your premier partner for hotel &amp; housekeeping supplies.
            </h1>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-xl mb-8" data-testid="hero-subtitle">
              From premium cleaning chemicals to furniture, amenities and manpower — Pranav Anand Enterprises equips hotels, restaurants and facilities with everything they need. Sign in to place customised orders and track every invoice.
            </p>
            <div className="flex flex-wrap gap-3 justify-center md:justify-start">
              <Link to="/login">
                <Button size="lg" className="rounded-sm bg-[#002fa7] hover:bg-[#00227b] text-base px-10 py-7 h-auto font-medium" data-testid="cta-signin">
                  Sign in to order
                  <ArrowRight className="w-5 h-5 ml-2" strokeWidth={2} />
                </Button>
              </Link>
              <Link to="/catalog">
                <Button size="lg" variant="outline" className="rounded-sm text-base px-8 py-7 h-auto font-medium border-gray-300" data-testid="cta-explore">
                  Explore products
                </Button>
              </Link>
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="border border-gray-200 bg-white p-6">
              <div className="grid grid-cols-2 gap-3">
                {BENEFITS.map(({ i: Icon, t, d }) => (
                  <div key={t} className="border border-gray-200 p-4">
                    <Icon className="w-5 h-5 text-[#002fa7] mb-3" strokeWidth={1.5}/>
                    <div className="font-heading font-medium text-sm">{t}</div>
                    <div className="text-xs text-gray-500 mt-1">{d}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What we offer */}
      <section id="products" className="border-t border-gray-200 bg-gray-50" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-16 md:py-20">
          <p className="text-xs uppercase tracking-[0.2em] text-[#0e5a32] mb-3 font-medium">What we offer</p>
          <h2 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight text-gray-900 mb-3">A spectrum of quality, under one roof</h2>
          <p className="text-sm md:text-base text-gray-600 max-w-2xl mb-10">Comprehensive solutions for hotels, restaurants and housekeeping operations — premium products paired with reliable service.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {CATEGORIES.map(({ i: Icon, t, items }) => (
              <div key={t} className="bg-white border border-gray-200 p-6 hover:border-[#0e5a32] transition-colors" data-testid={`category-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="w-11 h-11 flex items-center justify-center bg-[#0e5a32]/10 mb-4">
                  <Icon className="w-6 h-6 text-[#0e5a32]" strokeWidth={1.5}/>
                </div>
                <h3 className="font-heading font-medium text-lg text-gray-900 mb-3">{t}</h3>
                <ul className="space-y-2">
                  {items.map((it) => (
                    <li key={it} className="flex items-start gap-2 text-sm text-gray-600">
                      <CheckCircle2 className="w-4 h-4 text-[#002fa7] shrink-0 mt-0.5" strokeWidth={1.5}/>
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About us */}
      <section id="about" className="border-t border-gray-200 bg-white" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-16 md:py-20 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6 order-2 md:order-1">
            <p className="text-xs uppercase tracking-[0.2em] text-[#0e5a32] mb-3 font-medium">About us</p>
            <h2 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight text-gray-900 mb-4">Elevating standards, delivering excellence.</h2>
            <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-4">
              Founded in <strong>2020</strong>, Pranav Anand Enterprises has rapidly established itself as a leading provider of comprehensive solutions for hotels, restaurants and housekeeping operations. We are dedicated to equipping your establishment with the highest quality products and services — ensuring seamless operations and a superior experience for your guests and staff.
            </p>
            <div className="border-l-2 border-[#0e5a32] pl-4 py-1 bg-gray-50">
              <p className="text-xs uppercase tracking-wider text-[#0e5a32] font-semibold mb-1">Our mission</p>
              <p className="text-sm text-gray-700 leading-relaxed">
                To provide exceptional services to commercial and government facilities through focused efficiency and customer satisfaction, embodying integrity in all our operations. We deliver comprehensive, end-to-end solutions by combining premium products with efficient, reliable service delivery.
              </p>
            </div>
          </div>
          <div className="md:col-span-6 order-1 md:order-2">
            <div className="rounded-md overflow-hidden border border-gray-200">
              <img
                src="https://images.unsplash.com/photo-1664382953403-fc1ac77073a0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="Pranav Anand Enterprises — supplies and operations"
                className="w-full h-64 md:h-80 object-cover"
                data-testid="about-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="border-t border-gray-200 bg-[#0e5a32]" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-14">
          <div className="grid md:grid-cols-2 gap-10 items-start">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-white/70 mb-3 font-medium">Contact us</p>
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-white leading-tight">Need help with an order or bill?</h2>
              <p className="text-sm text-white/80 mt-3 max-w-md">Our support team is here to help with orders, invoices and your account. Reach out any time.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <a href="tel:+918850030301" className="flex items-center gap-3 bg-white/10 hover:bg-white/15 transition-colors rounded-md p-4 border border-white/15" data-testid="contact-phone">
                <Phone className="w-5 h-5 text-white shrink-0" strokeWidth={1.5}/>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-white/60">Phone</div>
                  <div className="text-sm font-medium text-white">+91 88500 30301</div>
                </div>
              </a>
              <a href="mailto:pranav.anand.enterprises6@gmail.com" className="flex items-center gap-3 bg-white/10 hover:bg-white/15 transition-colors rounded-md p-4 border border-white/15" data-testid="contact-email">
                <Mail className="w-5 h-5 text-white shrink-0" strokeWidth={1.5}/>
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-wider text-white/60">Email</div>
                  <div className="text-sm font-medium text-white truncate">pranav.anand.enterprises6@gmail.com</div>
                </div>
              </a>
            </div>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 md:px-8 py-5 text-xs text-white/60">
            © {new Date().getFullYear()} Pranav Anand Enterprises. Elevating standards, delivering excellence.
          </div>
        </div>
      </section>
    </div>
  );
}
